import { useState } from 'react'
import { useTranslation } from 'react-i18next'
// Imported directly (not from '../../components') so this module doesn't
// pull in the barrel's i18next-initializing components under a mocked
// react-i18next in tests.
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { setRemMaxPackingTime } from '../../services/orderServices'

// Backend 422 code for a manually-set deadline that's malformed or in the past.
const INVALID_DATE_ERROR_CODE = 'invalid_or_past_deadline'

// ConfirmDialog has no children slot — its `description` prop renders whatever
// node it's given, so the input + error live there instead of a bespoke dialog.
export const SetRemPackingTimeDialog = ({ order, open, onClose, onSaved }) => {
    const { i18n } = useTranslation()
    const [value, setValue] = useState("")
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)

    const errorMessageFor = (e) => {
        const code = e?.response?.data?.error
        if (code === INVALID_DATE_ERROR_CODE) return i18n.t('list_orders.rem_packing_time.error_invalid_date')
        return i18n.t('list_orders.rem_packing_time.error')
    }

    const handleConfirm = async () => {
        if (!value) {
            setError(i18n.t('list_orders.rem_packing_time.error'))
            return
        }
        const parsed = new Date(value)
        if (Number.isNaN(parsed.getTime())) {
            setError(i18n.t('list_orders.rem_packing_time.error_invalid_date'))
            return
        }
        setSaving(true)
        setError(null)
        try {
            await setRemMaxPackingTime(order.id, parsed.toISOString())
            // Await the refetch before closing: an unawaited onSaved() left a
            // window where the row still offered "Definir" and a second click
            // could 422 against an already-set deadline. The dialog unmounts
            // right after, so no setSaving(false) on this path — that would
            // fire post-unmount.
            await onSaved()
            onClose()
        } catch (e) {
            setSaving(false)
            setError(errorMessageFor(e))
        }
    }

    return (
        <ConfirmDialog
            open={open}
            setOpen={onClose}
            onCancel={onClose}
            title={i18n.t('list_orders.rem_packing_time.dialog_title', { order_number: order.order_number })}
            description={(
                <div>
                    <p>{i18n.t('list_orders.rem_packing_time.dialog_description')}</p>
                    <input
                        data-testid="rem-packing-time-input"
                        type="datetime-local"
                        className="mt-2 border rounded px-2 py-1 w-full"
                        value={value}
                        min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                        onChange={(e) => { setValue(e.target.value); setError(null) }}
                    />
                    {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
                </div>
            )}
            confirmLabel={i18n.t('list_orders.rem_packing_time.confirm')}
            cancelLabel={i18n.t('list_orders.rem_packing_time.cancel')}
            loadingLabel={i18n.t('list_orders.rem_packing_time.saving')}
            onConfirm={handleConfirm}
            loading={saving}
        />
    )
}
